{/*
  Lessons 38–45: the post-Duolingo verbal system (one lesson a week; lessons 46–48 sentence and word,
  49–51 the Targaryen finale). Merged weeks since 2026-10-05 (docs/COURSE.md has the old → new map).
  Ordered from most to least useful for reading show dialogue and writing letters to each other.
  Duolingo (lessons 02 and 04–37) already covered: present, perfect, future and imperfect (active, both moods),
  negation with the subjunctive, jaelagon + subjunctive, the -ks passive (present/perfect/future),
  basic infinitive constructions (kostagon, sytilībagon, bēvilagon, sahagon), vocative jussive and
  infinitive + daor, the three valence prefixes, -ākogon with a locative object, impersonal davābagon.

  38 Aoristi – yleiset totuudet (Valar morghūlis): aorist active ind./subj., aorist infinitive, a first look at the aorist passive
  39 Subjunktiivi sivulauseissa: lo-ehdot, epätodelliset ehdot, tarkoitus (kessyt, hegnīr), toive, mahdollisuus (kostilus), epäsuora esitys
  40 Partisiipit: preesensin, aoristin ja futuurin aktiivi; perfektin, aoristin, futuurin ja menneen toistuvan passiivi; partisiippilausekkeet
  41 Pluskvamperfekti ja mennyt toistuva: menneen ajan järjestelmä loppuun (aika × aspekti -taulukko); kirjeet (was 63)
  42 Infinitiivit: aspektin ja pääluokan mukaan; infinitiivi nominina, tarkoituksena ja epäsuorana käskynä
     + Käskyt, kehotukset ja toivotukset: futuurin ja aoristin imperatiivi, passiivin imperatiivi, datiivi + infinitiivi (ynot rēbagon), īlot/īlos, toivotukset
  43 Verbiluokat, perfektivartalot ja epäsäännölliset verbit: -za/-sa/-as, perfektivartalon nyrkkisäännöt, sagon, ilagon, jagon, emagon, sahagon, gaomagon
  44 Passiivi loppuun ja persoonattomat rakenteet: passiivi kaikissa aikamuodoissa, passiivin rajat, persoonaton kostagon/vestragon, suomen passiivin vertailu
  45 Verbin etuliitteet: applikatiivien muunnelmat, adpositioetuliitteet hen-, bē-, gō-, va-, syt-; jor-, mī-, maz-, nā-, qrin-, oz-, ā-
     + Verbinjohtimet: -emagon, -ikagon/-kagon, -ūljagon, -ñagon, -ākogon, -uragon, eventatiivi -egon, toisto -ligon
*/}

export const meta = {
  id: '38-aorist',
  order: 38,
  checkFocus: { tense: ['aorist'] },   // what the end-of-lesson check tests first
  titleFi: 'Aoristi: aina totta',
  titleEn: 'The aorist',
  lemmaIds: [
    'rhovagon', 'lilagon', 'zūgagon', 'laodigon', 'ossēnagon', 'rhaenagon', 'rhakitegon',
    'jomōzugon', 'iāragon', 'ojāragon', 'ivīlībagon', 'imorghūljagon',
    'sīmonagon', 'mazilībagon', 'udligon',
    'ours:qrīdropakio', 'ours:virvos',
    'ours:virtetir',
    'ours:ūbrilnon',
    'ours:allergenka',
    'ours:ondurillōñe',
    'ours:pāsāptir',
    'ours:positivenka',
    'ours:negativenka',
    'ours:ironenka',
    'ours:klassenka',
  ],
  exampleIds: [
    'got-s3e06-001', 'got-s3e06-002', 'ex-e7241a89da5f', 'ex-a94e383d0e76', 'ex-7c7e8e1e0090',
    'got-s3e06-008', 'got-s5e09-003', 'got-s4e03-005', 'hotd-s3e08-002', 'hotd-s1e07-003',
    'ex-f5e4623aa8f6', 'ex-809b34b2bbbb', 'got-s5e04-005', 'ex-411f5abc7bd4', 'ex-93fc345af78b',
    'hotd-s3e02-002', 'ex-59c1a46a84a1', 'got-s7e02-005', 'got-s3e06-012', 'ex-d01928b2ea35',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Conjugation',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Tables',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://dedalvs.tumblr.com/post/181114607984/hi-david-this-hv-duolingo-course-is',
    'https://dothraki.com/2014/05/valar-javaris/#comment-26621',
    'https://archiveofourown.org/works/41199249/chapters/105725109',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['aorist'],
  grammarAlso: ['negation'],
  drills: { tense: ['aorist'] },
  showLines: ['got-s3e06-001', 'got-s3e06-002', 'got-s3e06-008', 'got-s5e09-003', 'got-s4e03-005', 'hotd-s3e08-002', 'hotd-s1e07-003', 'got-s5e04-005', 'hotd-s3e02-002', 'got-s7e02-005', 'got-s3e06-012'],
  pronunciation: { focus: 'long-vowels', words: ['zūgagon', 'ossēnagon', 'jomōzugon', 'iāragon', 'ojāragon'], lines: ['got-s3e06-001', 'got-s3e06-002', 'got-s3e06-008'] },
  letter: {
    fi: 'Kirjoita kolme yleistä totuutta itsestäsi (aoristi).',
    en: 'Write three general truths about yourself (aorist).',
  },
  scene: 'hotd-s3e02-1',
}

# Aoristi: aina totta

Duolingo on nyt takana, ja sen mukana neljä aikamuotoa: preesens, perfekti, futuuri ja imperfekti. Wikin kielioppisivun mukaan korkeavalyriassa on kuitenkin **seitsemän** aikamuotoa. Puuttuvista kolmesta aoristi on ylivoimaisesti tärkein, ja olette kuulleet sitä sarjan ensimmäisestä valyriankielisestä lauseesta lähtien:

<Line id="got-s3e06-001" />

<Interlinear hv="Valar morghūl|is." gloss="ihminen-NOM.COL kuolla-AOR.3SG" fi="Kaikki ihmiset kuolevat." />

Tämän oppitunnin aihe on siis se, mikä tekee lauseesta *Valar morghūlis* sananlaskun eikä uutista.

## Mitä aoristi tarkoittaa

Wikin kielioppisivu määrittelee aoristin näin: sitä käytetään yleisistä totuuksista ja tosiasioista sekä tapahtumista, jotka toistuvat tavallisesti tai aina. Preesens taas kertoo siitä, mikä on käynnissä **juuri nyt** – ja wikin verbintaivutussivu sanoo suoraan, että korkeavalyrian preesensiä ei yleensä käytetä ajattomista asioista (”puhun valyriaa”) eikä yleisistä totuuksista (”yksi plus yksi on kaksi”). Ne kuuluvat aoristille.

Wikin esimerkkilauseissa ero näkyy kauneimmin näin:

<Example id="ex-e7241a89da5f" />

<Interlinear hv="Jaohossa rhov|is. Jaohossa rhov|isi." gloss="koira-NOM.PL haukkua-PRS.3PL koira-NOM.PL haukkua-AOR.3PL" fi="Koirat haukkuvat (nyt). Koirat haukkuvat (koirat ovat sellaisia)." />

Ensimmäinen lause kuvaa tilannetta pihalla, toinen koirien luontoa.

<Note>
**Suomi ei auta tässä.** Suomen preesens hoitaa molemmat merkitykset: ”koirat haukkuvat” voi olla uutinen tai luonnonlaki, ja ero jää asiayhteyden varaan. Englanti on lähempänä: *the dogs are barking* ~ preesens, *dogs bark* ~ aoristi. Kun mietitte, kumpaa käyttää, kysykää: ”Tapahtuuko tämä nyt, vai pitääkö se aina paikkansa?”
</Note>

<Note>
**Lajittele: nyt vai aina?** Damien Halme (Academy of High Valyrian) käyttää näitä kolmea esimerkkinä: *Näen lunta.* *Juoksen (juuri nyt).* *Kuulen jumalien äänen (aina).* Kaksi ensimmäistä ovat valyriaksi preesensiä, koska ne tapahtuvat nyt. Kolmas kertoo pysyvästä asiasta, joten se on aoristi.
</Note>

### Miksi ”all men must die”?

Englanninkielisissä käännöksissä *Valar X-is* -sanonnoissa on lähes aina sana *must*: *Valar morghūlis* ”all men must die”, *Valar dohaeris* ”all men must serve”. Valyriankielisessä lauseessa ei kuitenkaan ole mitään ”täytyy”-sanaa – verbi on tavallinen aoristi, ja koska aoristi ilmaisee yleistä totuutta, englanti tavoittaa sävyn parhaiten *must*-sanalla. Suomeksi ”kaikki ihmiset kuolevat” on lähempänä alkuperäistä kuin ”kaikkien ihmisten täytyy kuolla”.

<Line id="got-s3e06-002" />

<Interlinear hv="Valar dohaer|is." gloss="ihminen-NOM.COL palvella-AOR.3SG" fi="Kaikki ihmiset palvelevat." />

Samalla kaavalla on tehty muitakin sanontoja; wikin esimerkeissä on muun muassa nämä:

<Example id="ex-a94e383d0e76" />

<Example id="ex-7c7e8e1e0090" />

Huomatkaa, että *valar* on kollektiivi, ja kollektiivi saa verbin **yksikköön** (tämä tuli vastaan jo kollektiiveja ja luontoa käsitelleillä oppitunneilla). Siksi verbi on yksikön 3. persoonassa: <Hv>dohaeris</Hv>, <Hv>limassis</Hv>, <Hv l="lilagon">lilis</Hv>.

<Note>
**Kaksitulkintainen *morghūlis*.** Verbintaivutussivu huomauttaa, että <Hv>morghūljagon</Hv>-verbin preesensin monikon 3. persoona menettää liudennuksensa ja on siksi täsmälleen samanlainen kuin aoristin yksikön 3. persoona: <Hv>morghūlis</Hv> voi olla kumpi tahansa. Koska *valar* on kollektiivi ja vaatii yksikön, sanonta on aoristissa. <Hv>Dohaeris</Hv> ei ole kaksitulkintainen: preesensin monikon 3. persoona olisi <Hv>dohaerzi</Hv>.
</Note>

<Note>
Miksi *j* katoaa? Damien Halmen (Academy of High Valyrian) selitys on helppo muistaa: *j* ja *i* ovat melkein sama äänne, joten *j* sulautuu seuraavaan *i*:hin. Siksi <Hv>morghūljagon</Hv> saa muodot <Hv glossed>morghūli</Hv> ’kuolemme’ ja <Hv>morghūlis</Hv>, ei *morghūljis*.
</Note>

Lannisterien tunnuslause on myös aoristissa:

<Example id="ex-d01928b2ea35" />

<Note>
Damien Halme (Academy of High Valyrian) huomauttaa, että aoristi esittää velan maksamisen yleisenä totuutena, juuri niin kuin Lannisterit haluavat sen kuultavan.
</Note>

Muistatko tunnuslauseen <LessonLink n={20}>oppitunnilta 20</LessonLink>? Silloin opettelit sen kokonaisena. Nyt näet, miksi verbi on <Hv l="ademmagon">ademmis</Hv>: se on aoristi, koska lause kertoo, mitä Lannister aina tekee.

## Muodostaminen

Aoristi on yksi säännöllisimmin muodostuvista aikamuodoista. Päätyyppejä on kaksi:

- **Konsonanttivartaloiset** verbit saavat vartalon perään *i*:n ja sen perään persoonapäätteen: <Seam l="jaelagon" s="jael" e="in" />, <Seam l="jaelagon" s="jael" e="ia" />, <Seam l="jaelagon" s="jael" e="is" />, <Seam l="jaelagon" s="jael" e="iti" />, <Seam l="jaelagon" s="jael" e="iat" />, <Seam l="jaelagon" s="jael" e="isi" />.
- **Vokaalivartaloiset** verbit saavat vartalovokaalin perään *-ssi-*: <Hv glossed>ȳdrassis</Hv> ’puhuu (tapanaan)’, <Hv glossed>nektossis</Hv> ’leikkaa’, <Hv glossed>limassis</Hv> ’itkee’, <Hv glossed>ēdrussis</Hv> ’nukkuu’.

<Paradigm lemma="jaelagon" filter="indicative.active.aorist" />

<Paradigm lemma="ȳdragon" filter="indicative.active.aorist" />

Huomatkaa, että vokaalivartaloisilla verbeillä monikon 3. persoona on lyhyt: <Hv>ȳdrassi</Hv>, ei *-ssisi*.

<Note>
**Wiki on epävarma monikon 1. persoonasta.** Verbintaivutussivun taulukossa monikon 1. persoona <Hv l="jaelagon" fi="toivomme">jaelī</Hv> on merkitty harmaalla eli pääteltynä, ei todistettuna, kun taas wikin taivutustaulukoissa – joiden säännöistä sovelluksen taulukot on muodostettu – muoto on <Hv l="jaelagon">jaeliti</Hv>. Sovellus näyttää jälkimmäisen. Kun kirjoitatte monikon 1. persoonan aoristia, muistakaa, että muoto on epävarma.
</Note>

### Vertailu suomeen

<Compare fi="tanssi|vat" hv="lil|isi" label="preesens ~ aoristi" />

Suomen *tanssivat* kattaa sekä merkityksen ”tanssivat nyt” että ”tanssivat (aina, yleensä)”. Valyriassa jälkimmäinen on aoristi <Hv>lilisi</Hv>, edellinen preesens <Hv>lilzi</Hv>.

## Aoristi sarjoissa

Sarjoissa aoristia käytetään paljon juuri siksi, että hahmot puhuvat periaatteista, uskosta ja luonteesta. Thoros ja Melisandre puhuvat uskonnostaan aoristissa:

<Line id="got-s3e06-008" />

<Interlinear hv="Aōhoso ziry rijīb|ia, se ñuhoso ziry rijīb|in." gloss="sinun-INS hän-ACC palvoa-AOR.2SG ja minun-INS hän-ACC palvoa-AOR.1SG" fi="Sinä palvot Häntä omalla tavallasi, ja minä omallani." />

Palvominen ei ole hetken teko vaan tapa elää – siksi aoristi. Sama repliikki jatkuu kysymyksellä, jossa on taas aoristi <Hv>ȳdrassis</Hv>: kielen puhuminen on taito, ei hetkellinen tapahtuma.

<Note>
Thorosin <Hv>ȳdrassis</Hv> on Petersonin mukaan virhe: valyriassa ei puhutella kohteliaasti kolmannessa persoonassa, joten oikein olisi 2. persoona <Hv>ȳdrassia</Hv> (Petersonin Tumblr-vastaus 2018; myös wikin alaviite).
</Note>

Thorosin vastauksessa Melisandrelle sama ajatus jatkuu: hän pyytää (aoristi <Hv l="jepagon" fi="pyydän">jepin</Hv>), ja jumala vastaa (aoristi <Hv>udlissis</Hv>) – näin asiat aina menevät.

<Line id="got-s3e06-012" />

Daenerys kuvaa vihollista aoristilla, koska kyse on vihollisen luonteesta eikä yksittäisestä teosta:

<Line id="got-s4e03-005" />

<Interlinear hv="Jevys qrinuntys jevor riñar laodi|ssis ossēn|īs." gloss="teidän-SOL.NOM vihollinen-NOM teidän lapsi-ACC.COL varastaa-AOR.3SG murhata-AOR.3SG" fi="Vihollisenne varastaa ja murhaa lapsianne." />

(Viimeisen sanan pitkä *ī* on <Term id="konjunktiivinen-pidennys">konjunktiivinen pidennys</Term>: se korvaa ja-sanan. Perusmuoto olisi <Hv>ossēnis</Hv>.)

<Note>
Neljännessä virkkeessä Daenerys sanoo *ēzi* (monikko), vaikka subjekti *jevys qrinuntys* on yksikössä. Peterson on myöntänyt virheen: oikein olisi <Hv>ēza</Hv> (dothraki.com 2014).
</Note>

Areenalla taistelija huutaa kuningattarelle valan, joka on voimassa aina:

<Line id="got-s5e09-003" />

<Interlinear hv="Aōhor jaqiarzir ivīlīb|in imorghūl|īn, jaqiarzus Dārȳs." gloss="sinun-AQ.ACC kunnia-ACC taistella.puolesta-AOR.1SG kuolla.puolesta-AOR.1SG=ja kunniakas-LUN.VOC kuningatar-VOC" fi="Taistelen ja kuolen kunniasi puolesta, oi kunniakas kuningatar." />

Daemon puhuu valtakunnan luonteesta:

<Line id="hotd-s3e08-002" />

Vaemond Velaryon taas puhuu suvun verestä – kaikki neljä verbiä ovat aoristeja, koska suku on sitä, mitä se on:

<Line id="hotd-s1e07-003" />

<Note>
Viimeisessä virkkeessä <Hv>bēvilis</Hv> on jo tutun <Hv>bēvilagon</Hv>-verbin aoristi: ”ei koskaan saa ohentua” – velvollisuus, joka pätee aina, ei vain nyt. Peterson on myöhemmin korjannut saman virkkeen *īlvon*-sanan: <Hv>bēvilagon</Hv>-verbin tekijä on genetiivissä, joten oikein olisi <Hv l="īlvon">īlvo</Hv> (Petersonin Conlang Dialogue -selitykset, 2022).
</Note>

### Tavat ja tottumukset

Aoristi on myös **tapojen** aikamuoto – juuri sitä tarvitsette kirjeissä, kun kerrotte arjestanne. Wikin esimerkki:

<Example id="ex-f5e4623aa8f6" />

<Interlinear hv="Sūbroma tubis rhaen|in." gloss="tee-COM päivä-ACC aloittaa-AOR.1SG" fi="Aloitan päiväni (aina) teellä." />

Preesens <Hv l="rhaenagon" fi="aloitan">rhaenan</Hv> tarkoittaisi, että aloitan päivää teellä juuri nyt.

## Kielteinen aoristi

Kieltolauseessa verbi on tuttuun tapaan subjunktiivissa. Aoristin subjunktiivissa *i*:n tilalla on *u*: konsonanttivartaloilla *-un, -ua, -us, -uty, -uat, -usy*, vokaalivartaloilla *-ssun, -ssua, -ssus* ja niin edelleen.

<Paradigm lemma="jaelagon" filter="subjunctive.active.aorist" />

<Example id="ex-809b34b2bbbb" />

<Interlinear hv="Zaldrīzesse biādroti elēnȳti zūg|usy daor." gloss="lohikäärme-NOM.PL mielipide-DAT.PL lammas-GEN.PL pelätä-AOR.SBJV.3PL ei" fi="Lohikäärmeet eivät pelkää lampaiden mielipiteitä." />

Daenerys julistaa orjuuden lopun aoristin subjunktiivilla – ei vain ”tänään ei ole orjia” vaan ”orjia ei ole” uutena, pysyvänä tilana:

<Line id="got-s5e04-005" />

Subjunktiivia käytetään myös *lo*-sivulauseissa (”jos”, ”kun”) – niistä seuraavalla oppitunnilla. Esimakuna Petersonin lause, jossa on peräti kaksi aoristin subjunktiivia:

<Example id="ex-411f5abc7bd4" />

## Aoristin infinitiivi

Aoristilla on oma infinitiivinsä: <Hv l="jaelagon">jaeligon</Hv>, <Hv>ȳdrassigon</Hv>. Se sopii, kun puhutaan taidosta tai yleisestä kyvystä:

<Example id="ex-93fc345af78b" />

Lauseessa on kaksi aoristia: <Hv glossed>jomōzussis</Hv> ’juo itsensä humalaan (jatkuvasti, tapanaan)’ ja <Hv glossed>ȳdrassigon</Hv> ’puhua (kieltä, taitona)’.

## Olla-verbin aoristi

Verbintaivutussivun mukaan <Hv>sagon</Hv>-verbin aoristi on korkeaa, ”jopa pedanttista” tyyliä, ja sen tilalla käytetään lähes aina preesensiä. Muodot ovat silti olemassa:

<Paradigm lemma="sagon" filter="indicative.active.aorist" />

Rhaenyra kuitenkin käyttää sitä:

<Line id="hotd-s3e02-002" />

<Interlinear hv="Drījī ēdrȳnta iks|in." gloss="todella väsynyt-NOM olla-AOR.1SG" fi="Olen niin väsynyt." />

Rhaenyra on kuningatar, ja korkea tyyli sopii hänen suuhunsa – wiki ei kuitenkaan kerro, miksi Peterson valitsi juuri tähän aoristin. (Sana <Hv l="ēdrȳñagon">ēdrȳnta</Hv> on partisiippi; niihin palataan <LessonLink n={40}>oppitunnilla 40</LessonLink>.)

## Aoristin passiivi – ensimmäinen vilkaisu

Aoristin passiivissa tuttu *-ks*-tunnus tulee *i*-vartalon perään: <Hv glossed>ipradiks</Hv> ’syödään (yleisesti)’. Wikin esimerkki rinnastaa aktiivin ja passiivin:

<Example id="ex-59c1a46a84a1" />

Tärkein arkikäyttö on kuitenkin oman nimen kertominen. Melisandre esittäytyy:

<Line id="got-s7e02-005" />

<Interlinear hv="Melisandrose brōz|iks." gloss="Melisandre-INS nimetä-AOR.PASS.1SG" fi="Minua kutsutaan Melisandreksi." />

Nimi on pysyvä asia, siksi aoristi; itse nimi on instrumentaalissa (<Hv glossed>brōzagon</Hv> ’nimetä jollakin’). Passiivin muihin aikamuotoihin palataan <LessonLink n={44}>oppitunnilla 44</LessonLink>.

## Harjoituslauseita

<Practice hv="Taoba sīr lilza." fi="Poika tanssii nyt." />

<Practice hv="Taoba lilis." fi="Poika tanssii (tanssiminen on hänen juttunsa)." />

<Practice hv="Valyrio udrirzi ȳdrassin." fi="Puhun valyrian kieltä (osaan sitä)." />

<Practice hv="Zaldrīzesse lilisi." fi="Lohikäärmeet tanssivat (se on niiden tapa)." />

## Muistilista

- Preesens = käynnissä nyt. Aoristi = yleinen totuus, luonne, tapa, taito.
- Konsonanttivartalo + *i* + pääte (<Hv l="jaelagon">jaelis</Hv>); vokaalivartalo + *ssi* + pääte (<Hv>ȳdrassis</Hv>).
- Kielteinen aoristi: *u*-subjunktiivi + *daor* (<Hv fi="eivät pelkää">zūgusy daor</Hv>).
- *Valar morghūlis* = aoristi, ei ”täytyy”.

## Sanasto

<Vocab />

## Tarkistus

<Check />
